import React from 'react';
import { Alert, Empty, Select, Skeleton } from './basics';

export function Pager({ data, page, setPage, pageSize, setPageSize, noun = 'results' }) {
  if (!data) return null;
  const { count, total_pages: total } = data;
  const from = count ? (page - 1) * pageSize + 1 : 0;
  const to = Math.min(page * pageSize, count);
  const window = [];
  for (let p = Math.max(1, page - 2); p <= Math.min(total, page + 2); p += 1) window.push(p);

  return (
    <div className="pager">
      <span className="num">{from}–{to} of {count.toLocaleString()} {noun}</span>
      {setPageSize && (
        <Select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
          options={[10, 25, 50, 100].map((n) => ({ value: n, label: `${n} / page` }))} aria-label="Rows per page" />
      )}
      <div className="pages">
        <button disabled={page <= 1} onClick={() => setPage(page - 1)} aria-label="Previous page">‹</button>
        {window[0] > 1 && <><button onClick={() => setPage(1)}>1</button>{window[0] > 2 && <span>…</span>}</>}
        {window.map((p) => <button key={p} className={p === page ? 'on' : ''} onClick={() => setPage(p)}>{p}</button>)}
        {window[window.length - 1] < total && <>{window[window.length - 1] < total - 1 && <span>…</span>}
          <button onClick={() => setPage(total)}>{total}</button></>}
        <button disabled={page >= total} onClick={() => setPage(page + 1)} aria-label="Next page">›</button>
      </div>
    </div>
  );
}

/** columns: [{ key, label, render?(row), sortable?, align?, width? }]. `paged` is the object from usePaged. */
export function DataTable({ columns, paged, rowKey, onRowClick, empty, noun }) {
  const { data, loading, error, ordering, sortBy, page, setPage, pageSize, setPageSize } = paged;
  const rows = data ? data.results : [];
  const active = ordering.replace('-', '');

  return (
    <>
      {error && <div style={{ padding: 16 }}><Alert>{error}</Alert></div>}
      <div className="table-wrap" aria-busy={loading}>
        <table className="table">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} className={`${c.sortable ? 'sortable' : ''} ${c.align === 'right' ? 'r' : ''}`} style={{ width: c.width }}
                  onClick={c.sortable ? () => sortBy(c.key) : undefined} aria-sort={active === c.key ? (ordering.startsWith('-') ? 'descending' : 'ascending') : undefined}>
                  {c.label}{active === c.key && <span className="arrow">{ordering.startsWith('-') ? '↓' : '↑'}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && !data && [0, 1, 2, 3, 4].map((i) => (
              <tr key={i}>{columns.map((c) => <td key={c.key}><Skeleton w={`${50 + ((i + c.key.length) % 4) * 12}%`} /></td>)}</tr>
            ))}
            {rows.map((row) => (
              <tr key={rowKey(row)} className={onRowClick ? 'clickable' : ''} style={{ opacity: loading ? 0.55 : 1 }}
                onClick={onRowClick ? () => onRowClick(row) : undefined}>
                {columns.map((c) => (
                  <td key={c.key} className={c.align === 'right' ? 'r num' : ''}>{c.render ? c.render(row) : row[c.key] ?? '—'}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && data && rows.length === 0 && (empty || <Empty title="No results">Try changing or clearing the filters.</Empty>)}
      </div>
      <Pager data={data} page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} noun={noun} />
    </>
  );
}
